import { useEffect, useRef } from "react";

export interface NotificationEvent {
  type: string;
  payload: Record<string, unknown>;
}

/** Parse one SSE `data:` line into a NotificationEvent, or null if it's
 *  not valid JSON / not an object. Kept pure for testability. */
export function parseNotificationEvent(raw: string): NotificationEvent | null {
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    const rec = obj as Record<string, unknown>;
    // Some frames wrap their fields in `payload` (subagent_event); others put
    // them at the top level (job_event / artifact_event from the workflow
    // listener). Reading only `payload` gave subscribers an empty object for
    // top-level frames — ChatProvider's job_event match never fired and the
    // chat only updated on manual refresh.
    const { type: _t, payload, ...rest } = rec;
    return {
      type: String(rec.type ?? "unknown"),
      payload: (payload ?? rest) as Record<string, unknown>,
    };
  } catch {
    return null;
  }
}

/** Build the /v1/events URL. Identity rides the HttpOnly `egpt_session` cookie
 *  (EventSource sends cookies same-origin), so no token is in the URL. `lastId`
 *  is appended only for a CLIENT-forced reconnect (recreating the EventSource
 *  loses the native Last-Event-ID header); the backend treats it as the resume
 *  cursor so the Stream backlog isn't skipped. Kept pure for tests. */
export function buildEventStreamUrl(
  apiUrl: string,
  lastId: string | null,
  token?: string | null,
): string {
  const base = `${apiUrl.replace(/\/+$/, "")}/v1/events`;
  const q = new URLSearchParams();
  if (lastId) q.set("last_id", lastId);
  // EventSource cannot set an Authorization header, so the embed (bearer-only,
  // no cookies) authenticates the stream via ?token= — the backend's /v1/events
  // accepts it alongside the cookie path.
  if (token) q.set("token", token);
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

export interface UseEventStreamArgs {
  apiUrl: string;
  onEvent: (ev: NotificationEvent) => void;
  enabled?: boolean;
  /** Bearer for cookie-less contexts (embed) — sent as ?token= since
   *  EventSource cannot set headers. Omit for the SPA (cookie auth). */
  token?: string | null;
}

/** Subscribe to the unified_agent per-user notification stream
 *  (GET /v1/events). Degrades silently on failure — notifications are a
 *  convenience layer and must never block the chat session.
 *
 *  Note: EventSource cannot set custom headers; identity rides the HttpOnly
 *  session cookie, which the browser sends automatically on same-origin SSE. */
export function useEventStream({ apiUrl, onEvent, enabled = true, token = null }: UseEventStreamArgs) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  // Last event id seen across the session. The browser's auto-reconnect uses
  // the Last-Event-ID header (handled natively), but a forced recreate of the
  // EventSource (this effect re-running on a url change) starts a fresh
  // instance with no lastEventId — so we thread our tracked id via ?last_id=
  // to resume from the Stream backlog instead of skipping it.
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const url = buildEventStreamUrl(apiUrl, lastIdRef.current, token);
    let es: EventSource | null = null;
    try {
      es = new EventSource(url);
      es.onmessage = (e) => {
        // Track the id so a later forced reconnect can resume from here.
        if (e.lastEventId) lastIdRef.current = e.lastEventId;
        const ev = parseNotificationEvent(e.data);
        if (ev) onEventRef.current(ev);
      };
      es.onerror = () => {
        // Silent degrade; EventSource auto-reconnects (re-sends Last-Event-ID
        // header). No user-facing error.
      };
    } catch {
      // Environment without EventSource (tests/SSR) — no-op.
    }
    return () => { es?.close(); };
  }, [apiUrl, enabled, token]);
}
